<template>
    <div class="card ri-page" style="min-height: calc(100vh - 180px)">
        <PageHeader
            title="Recurring Invoice"
            subtitle="Bills due from each customer's Products & Services setup, grouped by billing date."
            @onClickCloseButton="onClickBack"
        />

        <div class="px-2 mt-2">
            <div class="ri-filter">
                <div class="row g-1 align-items-end">
                    <div class="col-12 col-sm-6 col-lg-3">
                        <label class="ri-label">From Date</label>
                        <div class="input-group">
                            <button type="button" class="btn btn-outline-secondary ri-step" title="Previous period" :disabled="loading" @click="onClickStep(-1)">
                                <span><i class="fas fa-chevron-left"></i></span>
                            </button>
                            <input v-model="fromDate" type="date" class="form-control ri-locked" readonly tabindex="-1"/>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-lg-3">
                        <label class="ri-label">To Date</label>
                        <div class="input-group">
                            <input v-model="toDate" type="date" class="form-control ri-locked" readonly tabindex="-1"/>
                            <button type="button" class="btn btn-outline-secondary ri-step" title="Next period" :disabled="loading" @click="onClickStep(1)">
                                <span><i class="fas fa-chevron-right"></i></span>
                            </button>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-lg-3">
                        <label class="ri-label">Interval</label>
                        <select v-model="interval" class="form-select" @change="onChangeInterval">
                            <option v-for="option in intervalOptions" :key="option.id" :value="option.id">{{ option.name }}</option>
                        </select>
                    </div>
                    <div class="col-auto">
                        <button
                            type="button"
                            style="min-width: 64px"
                            class="btn btn-primary waves-effect waves-float waves-light"
                            :disabled="loading"
                            @click="onClickSearch"
                        >Refresh</button>
                    </div>
                </div>
                <div v-if="periodLabel" class="ri-period-label mt-50">
                    <span><i class="fas fa-calendar-alt me-50"></i></span>{{ periodLabel }}
                </div>
                <div class="ri-status mt-1">
                    <button
                        v-for="statusTab in statusTabs"
                        :key="statusTab.id"
                        type="button"
                        class="ri-status-pill"
                        :class="{'ri-status-pill--active': activeStatus === statusTab.id}"
                        @click="onChangeStatus(statusTab.id)"
                    >
                        <span>{{ statusTab.name }}</span>
                        <span v-if="activeStatus === statusTab.id && searched" class="ri-status-count">{{ totalInvoices }}</span>
                    </button>
                    <span v-if="searched && tabs.length" class="ri-status-summary">
                        {{ totalInvoices }} invoice(s) on {{ tabs.length }} billing date(s) · Total {{ formatCurrency2D(totalAmount) }}
                    </span>
                </div>
            </div>

            <div v-if="tabs.length" class="ri-date-tabs mt-2">
                <button
                    v-for="tab in tabs"
                    :key="tabKey(tab)"
                    type="button"
                    class="ri-date-tab"
                    :class="{'ri-date-tab--active': isActiveTab(tab)}"
                    @click="selectTab(tab)"
                >
                    <span class="ri-date-tab-date">{{ formatTabDate(tab.billing_date) }}</span>
                    <span class="ri-date-tab-count">{{ tab.count }}</span>
                    <span class="ri-date-tab-label">invoice(s)</span>
                    <span class="ri-date-tab-amount">{{ formatCurrency2D(tab.total_amount) }}</span>
                </button>
            </div>

            <div v-if="searched && ! tabs.length && ! loading" class="ri-empty">
                <i class="fas fa-calendar-check ri-empty-icon"></i>
                <p class="mb-25 fw-bolder">No {{ activeStatus }} bills between {{ formatDate(fromDate) }} and {{ formatDate(toDate) }}</p>
                <p class="mb-0 ri-muted">Billing dates come from each customer's Products & Services setup (interval, billing day and billing start date).</p>
            </div>

            <div v-if="! searched && ! loading" class="ri-empty">
                <i class="fas fa-file-invoice ri-empty-icon"></i>
                <p class="mb-0 ri-muted">Pick a date range and press Go to see the recurring bills.</p>
            </div>

            <div v-if="activeTab" class="ri-panel mt-2">
                <div class="ri-panel-head">
                    <div>
                        <span class="ri-panel-date">{{ formatTabDate(activeTab.billing_date) }}</span>
                        <span class="ri-period ms-50">Bill period {{ formatDate(fromDate) }} to {{ formatDate(toDate) }}</span>
                    </div>
                    <input v-model="customerSearch" type="search" class="form-control ri-search" placeholder="Search customer"/>
                </div>

                <div v-if="activeStatus === 'pending'" class="table-responsive">
                    <table class="table ri-table mb-0">
                        <thead>
                        <tr>
                            <th class="text-center ri-check-col">
                                <input type="checkbox" class="form-check-input" :checked="isAllVisibleChecked" :disabled="! visibleItemIds.length" @change="toggleAllVisible"/>
                            </th>
                            <th>Customer / Product</th>
                            <th class="text-end">Rate</th>
                            <th class="text-end">VAT</th>
                            <th class="text-end">Total</th>
                        </tr>
                        </thead>
                        <tbody>
                        <template v-for="bill in filteredBills" :key="bill.contact_profile_id">
                            <tr class="ri-customer-row" :class="{'ri-row--selected': isCustomerChecked(bill)}">
                                <td class="text-center">
                                    <input type="checkbox" class="form-check-input" :checked="isCustomerChecked(bill)" @change="toggleCustomer(bill)"/>
                                </td>
                                <td>
                                    <button type="button" class="ri-expand" @click="toggleCollapse(bill)">
                                        <span v-if="isCollapsed(bill)"><i class="fas fa-chevron-right"></i></span>
                                        <span v-else><i class="fas fa-chevron-down"></i></span>
                                    </button>
                                    <span class="fw-bolder">{{ bill.customer_name }}</span>
                                    <span class="ri-muted ms-50">{{ bill.items.length }} product(s)</span>
                                </td>
                                <td class="text-end fw-bolder">{{ formatCurrency2D(bill.bill_amount) }}</td>
                                <td class="text-end fw-bolder">{{ formatCurrency2D(bill.vat_amount) }}</td>
                                <td class="text-end fw-bolder">{{ formatCurrency2D(bill.total_amount) }}</td>
                            </tr>
                            <template v-if="! isCollapsed(bill)">
                                <tr v-for="item in bill.items" :key="item.item_id" class="ri-item-row" :class="{'ri-row--selected': isItemChecked(item)}">
                                    <td class="text-center">
                                        <input type="checkbox" class="form-check-input" :checked="isItemChecked(item)" @change="toggleItem(item)"/>
                                    </td>
                                    <td class="ri-item-name">{{ item.product_name }}</td>
                                    <td class="text-end">{{ formatCurrency2D(item.rate) }}</td>
                                    <td class="text-end">
                                        {{ formatCurrency2D(item.vat_amount) }}
                                        <span v-if="Number(item.vat)" class="ri-muted">({{ Number(item.vat) }}%)</span>
                                    </td>
                                    <td class="text-end">{{ formatCurrency2D(item.total_amount) }}</td>
                                </tr>
                            </template>
                        </template>
                        </tbody>
                    </table>
                    <p v-if="! filteredBills.length" class="text-center ri-muted my-2">{{ bills.length ? 'No customer matches the search' : 'No pending bill on this date' }}</p>
                </div>

                <div v-if="activeStatus === 'pending' && bills.length" class="ri-action-bar">
                    <div class="ri-action-summary">
                        <template v-if="selectedItemIds.length">
                            <strong>{{ selectedBills.length }}</strong> invoice(s) ·
                            <strong>{{ selectedItemIds.length }}</strong> product(s) ·
                            Total <strong>{{ formatCurrency2D(selectedTotal) }}</strong>
                        </template>
                        <span v-else class="ri-muted">Select the products to invoice</span>
                    </div>
                    <div class="ri-action-fields">
                        <div>
                            <label class="ri-label">Start Date</label>
                            <input v-model="startDate" type="date" class="form-control"/>
                        </div>
                        <div>
                            <label class="ri-label">End Date</label>
                            <input v-model="endDate" type="date" class="form-control" :min="startDate"/>
                        </div>
                        <div>
                            <label class="ri-label">Due Date & Time</label>
                            <input v-model="dueDate" type="datetime-local" class="form-control"/>
                        </div>
                        <button
                            type="button"
                            class="btn btn-primary ri-process"
                            :disabled="processing || ! selectedItemIds.length"
                            @click="onClickProcess"
                        >
                            <span v-if="processing" class="spinner-border spinner-border-sm me-50"></span>
                            {{ processing ? 'Processing...' : 'Process' }}
                        </button>
                    </div>
                </div>

                <div v-if="activeStatus === 'completed'" class="table-responsive">
                    <table class="table ri-table mb-0">
                        <thead>
                        <tr>
                            <th>Bill No</th>
                            <th>Customer</th>
                            <th class="text-end">Bill Amount</th>
                            <th class="text-end">VAT</th>
                            <th class="text-end">Total</th>
                            <th class="text-center">Period</th>
                            <th class="text-center">Payment</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="bill in filteredBills" :key="bill.sale_master_id">
                            <td class="fw-bolder">{{ bill.bill_number }}</td>
                            <td>{{ bill.customer_name }}</td>
                            <td class="text-end">{{ formatCurrency2D(bill.subtotal_amount) }}</td>
                            <td class="text-end">{{ formatCurrency2D(bill.vat_amount) }}</td>
                            <td class="text-end fw-bolder">{{ formatCurrency2D(bill.total_amount) }}</td>
                            <td class="text-center">{{ formatDate(bill.start_date) }} to {{ formatDate(bill.end_date) }}</td>
                            <td class="text-center">
                                <span class="ri-payment" :class="'ri-payment--' + bill.payment_status">{{ bill.payment_status }}</span>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                    <p v-if="! filteredBills.length" class="text-center ri-muted my-2">{{ bills.length ? 'No customer matches the search' : 'No completed bill on this date' }}</p>
                </div>
            </div>
        </div>
        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import format from 'date-fns/format'
import addDays from 'date-fns/addDays'
import addMonths from 'date-fns/addMonths'
import addWeeks from 'date-fns/addWeeks'
import addYears from 'date-fns/addYears'
import startOfWeek from 'date-fns/startOfWeek'
import endOfWeek from 'date-fns/endOfWeek'
import startOfMonth from 'date-fns/startOfMonth'
import endOfMonth from 'date-fns/endOfMonth'
import startOfQuarter from 'date-fns/startOfQuarter'
import lastDayOfQuarter from 'date-fns/lastDayOfQuarter'
import startOfYear from 'date-fns/startOfYear'
import endOfYear from 'date-fns/endOfYear'
import PageHeader from '@/components/atom/PageHeader.vue'
import Loader from '@/components/atom/LoaderComponent'
import handlePurchaseAndSales from '@/services/modules/purchase'
import handleInventory from '@/services/modules/inventory'
import useDate from '@/services/utils/day'
import { formatCurrency2D } from '@/services/utils/global'

const route = useRoute()
const router = useRouter()
const showError = inject('showError')
const showSuccess = inject('showSuccess')
const { endOfTodayForInput } = useDate()
const { fetchHome } = handleInventory()
const { fetchRecurringInvoiceTabs, fetchRecurringInvoiceBills, processRecurringInvoices } = handlePurchaseAndSales()

const statusTabs = [
    { id: 'pending', name: 'Pending' },
    { id: 'completed', name: 'Completed' }
]

const loading = ref(false)
const processing = ref(false)
const searched = ref(false)
const activeStatus = ref('pending')
const fromDate = ref(route.query.start || '')
const toDate = ref('')
const interval = ref('monthly')
const intervalOptions = ref([])
const tabs = ref([])
const activeTab = ref(null)
const bills = ref([])
const customerSearch = ref('')
const collapsedIds = ref([])
const selectedItemIds = ref([])
const startDate = ref('')
const endDate = ref('')
const dueDate = ref(endOfTodayForInput())

const sumBy = (rows, key) => rows.reduce((sum, row) => sum + Number(row[key] || 0), 0)

const totalInvoices = computed(() => sumBy(tabs.value, 'count'))

const totalAmount = computed(() => sumBy(tabs.value, 'total_amount'))

const filteredBills = computed(() => {
    const keyword = customerSearch.value.trim().toLowerCase()
    if (! keyword) return bills.value
    return bills.value.filter(bill => String(bill.customer_name || '').toLowerCase().includes(keyword))
})

const visibleItemIds = computed(() => {
    if (activeStatus.value !== 'pending') return []
    return filteredBills.value.flatMap(bill => bill.items.map(item => item.item_id))
})

const isAllVisibleChecked = computed(() => visibleItemIds.value.length > 0 && visibleItemIds.value.every(id => selectedItemIds.value.includes(id)))

const selectedBills = computed(() => {
    if (activeStatus.value !== 'pending') return []
    return bills.value.filter(bill => bill.items.some(item => selectedItemIds.value.includes(item.item_id)))
})

const selectedTotal = computed(() => bills.value
    .flatMap(bill => bill.items || [])
    .filter(item => selectedItemIds.value.includes(item.item_id))
    .reduce((sum, item) => sum + Number(item.total_amount || 0), 0))

const WEEK_OPTIONS = { weekStartsOn: 6 }

const halfYearRange = (date) => {
    const start = new Date(date.getFullYear(), date.getMonth() < 6 ? 0 : 6, 1)
    return { start, end: endOfMonth(addMonths(start, 5)) }
}

const PERIOD_RANGES = {
    daily: (date) => ({ start: date, end: date }),
    weekly: (date) => ({ start: startOfWeek(date, WEEK_OPTIONS), end: endOfWeek(date, WEEK_OPTIONS) }),
    monthly: (date) => ({ start: startOfMonth(date), end: endOfMonth(date) }),
    quarterly: (date) => ({ start: startOfQuarter(date), end: lastDayOfQuarter(date) }),
    half_yearly: halfYearRange,
    yearly: (date) => ({ start: startOfYear(date), end: endOfYear(date) })
}

const PERIOD_STEPS = {
    daily: (date, direction) => addDays(date, direction),
    weekly: (date, direction) => addWeeks(date, direction),
    monthly: (date, direction) => addMonths(date, direction),
    quarterly: (date, direction) => addMonths(date, direction * 3),
    half_yearly: (date, direction) => addMonths(date, direction * 6),
    yearly: (date, direction) => addYears(date, direction)
}

const PERIOD_LABELS = {
    daily: (date) => format(date, 'EEE, d-MMM-yyyy'),
    weekly: (date) => 'Week of ' + format(date, 'd-MMM-yyyy'),
    monthly: (date) => format(date, 'MMM yyyy'),
    quarterly: (date) => 'Q' + format(date, 'Q yyyy') + ' (' + format(date, 'MMM') + ' – ' + format(lastDayOfQuarter(date), 'MMM') + ')',
    half_yearly: (date) => (date.getMonth() < 6 ? 'H1 ' : 'H2 ') + date.getFullYear() + (date.getMonth() < 6 ? ' (Jan – Jun)' : ' (Jul – Dec)'),
    yearly: (date) => format(date, 'yyyy')
}

const parseInputDate = (value) => new Date(value + 'T00:00:00')

const toInputDate = (date) => format(date, 'yyyy-MM-dd')

const anchorDate = () => fromDate.value ? parseInputDate(fromDate.value) : new Date()

const periodLabel = computed(() => {
    if (! fromDate.value) return ''
    const label = PERIOD_LABELS[interval.value] || PERIOD_LABELS.monthly
    return label(parseInputDate(fromDate.value))
})

const applyPeriod = (anchor) => {
    const range = (PERIOD_RANGES[interval.value] || PERIOD_RANGES.monthly)(anchor)
    fromDate.value = toInputDate(range.start)
    toDate.value = toInputDate(range.end)
    startDate.value = fromDate.value
    endDate.value = toDate.value
}

const defaultDueDate = (billingDate) => {
    const today = format(new Date(), 'yyyy-MM-dd')
    return (billingDate > today ? billingDate : today) + 'T23:59'
}

const companyQuery = () => '?company_id=' + route.params.companyId

const tabKey = (tab) => tab.billing_date + '|' + tab.interval

const isActiveTab = (tab) => activeTab.value !== null && tabKey(activeTab.value) === tabKey(tab)

const formatDate = (date) => {
    if (! date) return ''
    return format(new Date(date + 'T00:00:00'), 'd-MMM-yyyy')
}

const formatTabDate = (date) => {
    if (! date) return ''
    return format(new Date(date + 'T00:00:00'), 'EEE, d-MMM-yyyy')
}

const isItemChecked = (item) => selectedItemIds.value.includes(item.item_id)

const isCustomerChecked = (bill) => bill.items.every(item => isItemChecked(item))

const isCollapsed = (bill) => collapsedIds.value.includes(bill.contact_profile_id)

const toggleCollapse = (bill) => {
    collapsedIds.value = isCollapsed(bill)
        ? collapsedIds.value.filter(id => id !== bill.contact_profile_id)
        : [...collapsedIds.value, bill.contact_profile_id]
}

const toggleItem = (item) => {
    selectedItemIds.value = isItemChecked(item)
        ? selectedItemIds.value.filter(id => id !== item.item_id)
        : [...selectedItemIds.value, item.item_id]
}

const toggleIds = (itemIds, checked) => {
    const otherIds = selectedItemIds.value.filter(id => ! itemIds.includes(id))
    selectedItemIds.value = checked ? otherIds : [...otherIds, ...itemIds]
}

const toggleCustomer = (bill) => toggleIds(bill.items.map(item => item.item_id), isCustomerChecked(bill))

const toggleAllVisible = () => toggleIds(visibleItemIds.value, isAllVisibleChecked.value)

const resetTab = () => {
    activeTab.value = null
    bills.value = []
    selectedItemIds.value = []
    collapsedIds.value = []
    customerSearch.value = ''
}

const tabsQuery = () => {
    return companyQuery() + '&status=' + activeStatus.value + '&from_date=' + fromDate.value + '&to_date=' + toDate.value + '&interval=' + interval.value
}

const getTabs = async () => {
    loading.value = true
    tabs.value = []
    resetTab()
    try {
        const res = await fetchRecurringInvoiceTabs(tabsQuery())
        searched.value = true
        if (! res.status) {
            showError(res.message)
            return
        }
        tabs.value = res.data
        if (! tabs.value.length) return
        await selectTab(tabs.value[0])
    } catch (err) {
        showError('Something is wrong. Check your connectivity!!')
    } finally {
        loading.value = false
    }
}

const selectTab = async (tab) => {
    const query = companyQuery() + '&status=' + activeStatus.value + '&billing_date=' + tab.billing_date + '&interval=' + tab.interval
    resetTab()
    activeTab.value = tab
    startDate.value = fromDate.value
    endDate.value = toDate.value
    dueDate.value = defaultDueDate(tab.billing_date)
    loading.value = true
    try {
        const res = await fetchRecurringInvoiceBills(query)
        if (! res.status) {
            showError(res.message)
            return
        }
        bills.value = res.data
    } catch (err) {
        showError('Something is wrong. Check your connectivity!!')
    } finally {
        loading.value = false
    }
}

const getIntervals = async () => {
    try {
        const res = await fetchHome()
        if (! res.data || ! res.data.billing_intervals) return
        intervalOptions.value = Object.keys(res.data.billing_intervals).map(id => ({ id, name: res.data.billing_intervals[id] }))
    } catch (err) {
        intervalOptions.value = []
    }
}

const onClickSearch = () => {
    getTabs()
}

const onChangeInterval = () => {
    applyPeriod(anchorDate())
    getTabs()
}

const onClickStep = (direction) => {
    const step = PERIOD_STEPS[interval.value] || PERIOD_STEPS.monthly
    applyPeriod(step(anchorDate(), direction))
    getTabs()
}

const onChangeStatus = (status) => {
    if (activeStatus.value === status) return
    activeStatus.value = status
    tabs.value = []
    searched.value = false
    resetTab()
    if (! fromDate.value || ! toDate.value) return
    getTabs()
}

const processError = () => {
    if (! selectedItemIds.value.length) return 'Select at least one product to invoice'
    if (! startDate.value || ! endDate.value) return 'Start Date and End Date are required'
    if (endDate.value < startDate.value) return 'End Date can not be before Start Date'
    return ''
}

const removeProcessedItems = () => {
    bills.value = bills.value
        .map(bill => {
            const items = bill.items.filter(item => ! selectedItemIds.value.includes(item.item_id))
            return {
                ...bill,
                items,
                bill_amount: sumBy(items, 'rate'),
                vat_amount: sumBy(items, 'vat_amount'),
                total_amount: sumBy(items, 'total_amount')
            }
        })
        .filter(bill => bill.items.length > 0)
    activeTab.value.count = bills.value.length
    activeTab.value.total_amount = sumBy(bills.value, 'total_amount')
    selectedItemIds.value = []
}

const onClickProcess = async () => {
    const error = processError()
    if (error) {
        showError(error)
        return
    }
    processing.value = true
    try {
        const res = await processRecurringInvoices({
            company_id: route.params.companyId,
            billing_date: activeTab.value.billing_date,
            interval: activeTab.value.interval,
            item_ids: selectedItemIds.value,
            start_date: startDate.value,
            end_date: endDate.value,
            due_date: dueDate.value ? dueDate.value.replace('T', ' ') : null
        })
        if (! res.status) {
            showError(res.message)
            return
        }
        showSuccess(res.message)
        removeProcessedItems()
    } catch (err) {
        showError('Something is wrong. Check your connectivity!!')
    } finally {
        processing.value = false
    }
}

const onClickBack = () => {
    router.push({name: 'service-sales-list', params: route.params, query: {start: route.query.start, end: route.query.end}})
}

onMounted(() => {
    getIntervals()
    applyPeriod(anchorDate())
    getTabs()
})
</script>

<style scoped>
.ri-page {
    color: #000;
}

.ri-page .form-control,
.ri-page .form-select {
    font-size: 15px;
    color: #000;
}

.ri-filter {
    background: #f8f9fc;
    border: 1px solid #e4e8f0;
    border-radius: 10px;
    padding: 1rem;
}

.ri-label {
    display: block;
    margin-bottom: 0.3rem;
    font-size: 14px;
    font-weight: 600;
    color: #000;
}

.ri-muted {
    color: #4b5563;
    font-size: 14px;
}

.ri-status {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.6rem;
}

.ri-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 1.1rem;
    border: 1px solid #cfd6e2;
    border-radius: 999px;
    background: #fff;
    color: #000;
    font-size: 15px;
    font-weight: 600;
}

.ri-status-pill--active {
    background: #5a8dee;
    border-color: #5a8dee;
    color: #fff;
}

.ri-status-count {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 999px;
    padding: 0 0.5rem;
    font-size: 13px;
}

.ri-status-summary {
    color: #000;
    font-size: 15px;
}

.ri-date-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
}

.ri-date-tab {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.2rem;
    min-width: 170px;
    padding: 0.75rem 1rem;
    background: #fff;
    border: 1px solid #dfe4ee;
    border-top: 4px solid #f0c36d;
    border-radius: 10px;
    color: #000;
    transition: box-shadow 0.15s, border-color 0.15s;
}

.ri-date-tab:hover {
    box-shadow: 0 3px 10px rgba(34, 41, 47, 0.1);
}

.ri-date-tab--active {
    border-color: #5a8dee;
    border-top-color: #5a8dee;
    background: #f2f6fe;
    box-shadow: 0 3px 12px rgba(90, 141, 238, 0.25);
}

.ri-date-tab-date {
    font-weight: 700;
    font-size: 15px;
}

.ri-date-tab-count {
    font-weight: 700;
    font-size: 32px;
    line-height: 1.1;
}

.ri-date-tab-label {
    font-size: 13px;
    color: #4b5563;
    margin-top: -0.15rem;
}

.ri-date-tab-amount {
    font-size: 15px;
    font-weight: 700;
    color: #000;
}

.ri-empty {
    text-align: center;
    padding: 2.5rem 1rem;
    border: 1px dashed #cfd6e2;
    border-radius: 10px;
    margin-top: 1rem;
    font-size: 15px;
}

.ri-empty-icon {
    font-size: 2.2rem;
    color: #9aa5b8;
    margin-bottom: 0.6rem;
}

.ri-panel {
    border: 1px solid #e4e8f0;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 1rem;
}

.ri-panel-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.6rem;
    padding: 0.75rem 1rem;
    background: #f8f9fc;
    border-bottom: 1px solid #e4e8f0;
}

.ri-panel-date {
    font-size: 17px;
    font-weight: 700;
}

.ri-period {
    font-size: 14px;
    color: #4b5563;
}

.ri-search {
    max-width: 260px;
}

.ri-table {
    color: #000;
    font-size: 15px;
}

.ri-table th,
.ri-table td {
    padding: 10px 14px !important;
    vertical-align: middle;
}

.ri-table th {
    background: #eef1f7;
    color: #000;
    font-size: 14px;
    font-weight: 700;
    white-space: nowrap;
}

.ri-check-col {
    width: 48px;
}

.ri-customer-row td {
    background: #f3f5fa;
    border-top: 1px solid #dfe4ee;
    font-size: 16px;
}

.ri-table td.ri-item-name {
    padding-left: 42px !important;
}

.ri-row--selected td {
    background: #e8f0fe;
}

.ri-row--selected td:first-child {
    box-shadow: inset 3px 0 0 #5a8dee;
}

.ri-expand {
    border: 0;
    background: transparent;
    color: #000;
    padding: 0 0.5rem 0 0;
}

.ri-action-bar {
    position: sticky;
    bottom: 0;
    z-index: 2;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.85rem 1rem;
    background: #fff;
    border-top: 1px solid #dfe4ee;
    box-shadow: 0 -4px 12px rgba(34, 41, 47, 0.08);
}

.ri-action-summary {
    font-size: 16px;
    color: #000;
}

.ri-action-fields {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 0.6rem;
}

.ri-process {
    min-width: 140px;
    font-size: 15px;
}

.ri-payment {
    display: inline-block;
    padding: 0.15rem 0.65rem;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 600;
    text-transform: capitalize;
    background: #eef1f6;
    color: #000;
}

.ri-payment--due {
    background: #fdecea;
    color: #b02a1c;
}

.ri-payment--partial {
    background: #fff4e5;
    color: #a86400;
}

.ri-payment--paid {
    background: #e2f6ec;
    color: #157347;
}

.ri-step {
    padding-left: 0.7rem;
    padding-right: 0.7rem;
}

.ri-locked[readonly] {
    background: #fff;
    cursor: default;
}

.ri-period-label {
    font-size: 15px;
    font-weight: 600;
    color: #000;
}

@media (max-width: 575.98px) {
    .ri-date-tab {
        flex: 1 1 calc(50% - 0.75rem);
        min-width: 0;
    }

    .ri-search {
        max-width: none;
    }

    .ri-action-fields,
    .ri-action-fields > div,
    .ri-process {
        width: 100%;
    }
}
</style>
